import { SocialShare } from "@quintype/components";
import get from "lodash/get";
import throttle from "lodash/throttle";
import { array, bool, func, number, object, string } from "prop-types";
import React, { useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { isMobile } from "react-device-detect";
import { HeroImage, SocialShareTemplate } from "../../../arrow";
import { CaptionAttribution } from "../../../arrow/components/Atoms/CaptionAttribution";
import { STATIC_KEYWORDS } from "../../../constants";
import { DfpComponent } from "../../ads/dfp-component";
import { BreadCrumb, Button, Headline, PublishDateAndTime, StoryAuthors, StoryAuthorImages } from "../../atoms";
import FortuneExchange from "../../atoms/FortuneExchange";
import LiveButton from "../../atoms/live-button";
import { useDeviceType } from "../../customHooks/useDeviceType";
import { useAiFaqData } from "../../customHooks/useAiFaqData";
import { useSummaryCheck } from "../../customHooks/useSummaryCheck";
import { STORY_HEADER_IN_VIEW } from "../../store/actions";
import { MagazineStoryHeaderCard } from "../MagazineStoryHeaderCard";
import SummaryCard from "../SummaryCard";
import { TextWithReadMore } from "../../molecules/TextWithReadMore";
import { hasShowAiSummaryFalse, isSummaryBlockedByOwner } from "../../utils";
import { getCategoryAndSubCategory, getTemplateName } from "../../../ga-utils";

import "./StoryHeader.m.css";
import BookReviewCard from "../BookReviewCard";
import BookReviewButton from "../../atoms/BookReviewButton";
import MoviewReviewCard from "../MovieReviewCard";
import ReviewButton from "../../atoms/ReviewButton";

const StoryHeader = ({
  showAd,
  story,
  headline,
  storyAuthorData,
  publishTime,
  readTime,
  showHeroImage,
  setShowHighlights,
  magazineDetails,
  showReview,
  showBookReview,
  isValidBookReview = false,
}) => {
  const dispatch = useDispatch();
  const deviceType = useDeviceType();
  const isDesktop = deviceType === "desktop";
  const storyHeaderRef = useRef(null);

  const qtStates = useSelector((state) => state);
  const qtState = get(qtStates, ["qt"], {});
  const qtConfig = get(qtState, ["config"], {});
  const networkId = get(qtState, ["config", "ads-config", "network_id"], {});
  const storyHeaderInView = get(qtStates, "storyHeaderInView", true);
  const storyTemplate = get(story, ["story-template"], "");
  const isLiveBlog = storyTemplate === "live-blog";
  const isMagazine = storyTemplate === "magazine";
  const isSponsoredStory = storyTemplate === "sponsored";
  const isBookReview = storyTemplate === "book-review";
  const isMovieReview = storyTemplate === "review";
  const isSummaryEligibleStory = ["text", "magazine", "book-review", "review"].includes(storyTemplate);
  const { summaryPresent, showSummary, setShowSummary, summaryIndex } = useSummaryCheck(story.cards);
  const faqUrl = get(qtConfig, ["publisher-attributes", "publisher-faq-url"], false);
  const publisherAttributes = get(qtConfig, ["publisher-attributes"], {});
  const isInhouseAiSummaryEnabled =
    get(qtConfig, ["publisher-attributes", "publisher-inhouse-ai-summary"], false) === true;
  const isSummaryBlockedByStoryAttribute =
    isInhouseAiSummaryEnabled && (hasShowAiSummaryFalse(story) || isSummaryBlockedByOwner(story, publisherAttributes));
  const storyId = get(story, ["id"], "") || get(story, ["story-content-id"], "");
  const {
    hasSummary,
    summary,
    headline: apiHeadline,
    heroImageS3Key: apiHeroImageS3Key,
    isLoading,
  } = useAiFaqData({
    faqUrl,
    storyId,
    enabled:
      isSummaryEligibleStory &&
      faqUrl !== false &&
      !summaryPresent &&
      isInhouseAiSummaryEnabled &&
      !isSummaryBlockedByStoryAttribute,
  });
  const hasApiSummary = hasSummary && Boolean(summary);
  const canUseApiSummaryFallback = !summaryPresent && isInhouseAiSummaryEnabled && !isSummaryBlockedByStoryAttribute;
  const shouldUseApiSummary = canUseApiSummaryFallback && hasApiSummary;
  const shouldShowSummary = !isSummaryBlockedByStoryAttribute && (summaryPresent || shouldUseApiSummary);
  // const shouldRenderSummaryButton =
  //   ((!isSponsoredStory && storyTemplate === "text") || isBookReview || isMagazine || isMovieReview) &&
  //   !isSummaryBlockedByStoryAttribute &&
  //   (summaryPresent || canUseApiSummaryFallback);
  const shouldRenderSummaryButton =
    storyTemplate === "text" && !isSummaryBlockedByStoryAttribute && (summaryPresent || canUseApiSummaryFallback);
  const isSummaryButtonEnabled = shouldShowSummary;
  const isSummaryButtonPending = !summaryPresent && canUseApiSummaryFallback && isLoading;
  const isAdfreePage = get(qtState, ["config", "subscriber"]) === "true";
  const fullWidth = (!showAd || isAdfreePage) && !isMagazine && !isBookReview ? "full-width" : "";

  const handleScroll = () => {
    const storyHeaderPosition = storyHeaderRef.current.getBoundingClientRect();
    if (storyHeaderPosition.bottom < 75 && storyHeaderInView) {
      dispatch({
        type: STORY_HEADER_IN_VIEW,
        payload: false,
      });
    } else if (storyHeaderPosition.bottom > 75 && !storyHeaderInView) {
      dispatch({
        type: STORY_HEADER_IN_VIEW,
        payload: true,
      });
    }
  };

  const trackStoryInteraction = (eventAction, callback) => {
    const pageType = "story-page";
    const { category, subCategory, contentId } = getCategoryAndSubCategory(window.location, pageType, qtConfig, story);
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: "dl_story_interactions",
      event_action: eventAction,
      category,
      sub_category: subCategory,
      content_id: contentId,
      template_name: getTemplateName(story?.["story-template"] || pageType),
      platform: window.innerWidth >= 768 ? "desktop" : "mobile",
    });
    if (typeof callback === "function") {
      callback();
    }
  };

  const handleHighlightsClick = () => {
    trackStoryInteraction("highlight_clicks", () => setShowHighlights(true));
  };

  const handleSummaryClick = () => {
    if (!isSummaryButtonEnabled) {
      return;
    }
    trackStoryInteraction("summary_click", () => setShowSummary(true));
  };

  const throttledHandleScroll = throttle(handleScroll, 200);
  useEffect(() => {
    window.addEventListener("scroll", throttledHandleScroll);
    return () => {
      window.removeEventListener("scroll", throttledHandleScroll);
      throttledHandleScroll.cancel();
    };
  }, [throttledHandleScroll]);

  useEffect(() => {
    if (showSummary) {
      document.body.classList.add("no-scroll-forSummary");
      document.body.classList.remove("scroll-forSummar");
    } else {
      document.body.classList.remove("no-scroll-forSummary");
      document.body.classList.add("scroll-forSummar");
    }
    return () => {
      document.body.classList.remove("scroll-forSummar");
      document.body.classList.remove("no-scroll-forSummary");
    };
  }, [showSummary]);

  const iconSize = { width: "18px", height: "18px" };

  if (!headline) return null;
  const isMagazineType = story["story-template"] === "magazine";

  const trackPopupSocialShareClick = (platform) => {
    if (typeof window !== "undefined" && window.dataLayer) {
      const pageType = "story-page";
      const { category, subCategory, contentId } = getCategoryAndSubCategory(
        window.location,
        pageType,
        qtConfig,
        story
      );

      window.dataLayer.push({
        event: "dl_story_interactions",
        event_action: "share_click",
        link_text: platform || "share",
        category,
        sub_category: subCategory,
        content_id: contentId,
        template_name: getTemplateName(story?.["story-template"] || pageType),
        platform: window.innerWidth >= 768 ? "desktop" : "mobile",
      });
    }
  };
  const isAuthorImageRequired = get(story, ["metadata", "story-attributes", "authorimagerequired", "0"], false);
  const SocialShareComponent = () => {
    return (
      <div id={`story-share-${story.id}`} className="content-style">
        <SocialShare
          template={(props) => (
            <SocialShareTemplate
              {...props}
              story={story}
              qtConfig={qtState.config}
              onShareClick={(platform) => trackPopupSocialShareClick(platform?.toLowerCase())}
            />
          )}
          fullUrl={encodeURI(story.url)}
          title={story.headline}
          shareIconId={`share-icon-story-share-${story.id}`}
          buttonLabel={"Share"}
        />
      </div>
    );
  };

  const isMagazineStoryWrapper = isMagazineType && magazineDetails;
  let adConfig = get(qtState, ["config", "ads-config", "ad_slots", qtState.pageType, "right_side"], {});
  if (qtState.pageType === "story-page" && qtState?.data?.story?.["story-template"] !== "text") {
    adConfig = get(
      qtState,
      ["config", "ads-config", "ad_slots", qtState?.data?.story?.["story-template"], "right_side"],
      {}
    );
  }

  const bookReviewAndAdLogic = () => {
    if (isBookReview && showBookReview && isValidBookReview) {
      return <BookReviewCard reviewInfo={story.metadata} />;
    } else if (!isAdfreePage && showAd && !isMagazineType) {
      return (
        <div styleName="right-section">
          <DfpComponent
            adStyleName="ad-style-300x250"
            id="story-page-right-mrec"
            path={`${networkId}/${adConfig}`}
            adSlotConfigName="mRec_ad"
            showText={true}
          />
        </div>
      );
    } else if (isMagazineStoryWrapper) {
      return <MagazineStoryHeaderCard story={story} magazineDetails={magazineDetails} />;
    } else {
      return null;
    }
  };

  let magazineImagePresent = false;
  magazineImagePresent = magazineDetails?.metadata?.["cover-image"]?.["cover-image-url"] || false;
  const bookStyleName = `${isMagazineStoryWrapper ? "" : "no-magazine-issue"} ${
    isBookReview ? "book-review-button-wrapper" : ""
  } ${isMovieReview ? "review-button-wrapper" : ""} ${
    isMagazineStoryWrapper && magazineImagePresent ? "magazone-image-present" : ""
  }`;

  return (
    <React.Fragment>
      <div className="container story-header-wrapper" styleName={`story-header-wrapper `}>
        <div styleName={`top-content`} ref={storyHeaderRef}>
          <div styleName={`left-section ${fullWidth} ${isMagazineType ? "magazine-template" : ""} ${bookStyleName}`}>
            <div>
              {!isSponsoredStory && !isBookReview && !isMovieReview ? (
                <div
                  styleName="breadcrumb-story"
                  style={{ textAlign: isAdfreePage && !isMagazine ? "center" : "left" }}
                >
                  <BreadCrumb type="story-page" story={story} />
                </div>
              ) : null}
              <LiveButton story={story} storyHeader={true} />
              {isBookReview && <BookReviewButton />}
              {isMovieReview && <ReviewButton />}
            </div>

            {isSponsoredStory ? (
              <div styleName="sponsored-logo">
                <FortuneExchange />
                <div styleName="sponsored-tag">{STATIC_KEYWORDS.SPONSORED}</div>
              </div>
            ) : null}

            <Headline
              type={1}
              customStyleName={`h1-style ${isMagazineType ? "magazine" : ""}`}
              customClassName="article-header-headline"
            >
              {headline}
            </Headline>
            <div styleName="content-wrapper">
              {isAuthorImageRequired &&
                isAuthorImageRequired !== "false" &&
                story["story-template"] !== "sponsored" && <StoryAuthorImages authors={storyAuthorData} />}
              <div styleName="content">
                <StoryAuthors
                  storyAuthor={storyAuthorData}
                  customStyleName={"authors-name"}
                  story={story}
                  config={qtState.config}
                  pageType="story-page"
                />
                <div styleName="date-and-time">
                  Last Updated:
                  <PublishDateAndTime publishTime={publishTime} />
                  {/* {readTime ? (
                    <>
                      <span>/</span>
                      <ReadTime time={readTime} />
                    </>
                  ) : (
                    ""
                  )} */}
                </div>
              </div>
              <div styleName="btns-wrapper">
                {isLiveBlog ? (
                  <Button
                    iconType="highlights"
                    iconHeight={iconSize.height}
                    iconWidth={iconSize.width}
                    customStyleName="story-header-btn"
                    onClickFunction={handleHighlightsClick}
                  >
                    Highlights
                  </Button>
                ) : (
                  shouldRenderSummaryButton && (
                    <Button
                      iconType="summary"
                      iconHeight={iconSize.height}
                      iconWidth={iconSize.width}
                      customStyleName="story-header-btn"
                      onClickFunction={handleSummaryClick}
                      ariaLabel={isSummaryButtonPending ? "Summary is loading" : "Open Summary"}
                      disabled={!isSummaryButtonEnabled}
                    >
                      Summarise
                    </Button>
                  )
                )}
                <SocialShareComponent />
              </div>
            </div>
          </div>
          {isDesktop && <>{bookReviewAndAdLogic()}</>}
        </div>
        {story?.subheadline && (
          <TextWithReadMore
            textElement={{ text: story?.subheadline }}
            deviceType={deviceType}
            isMagazine={isMagazineType}
            storyHeader={true}
          />
        )}
        {showReview && isMovieReview && <MoviewReviewCard reviewInfo={story.metadata} />}
        {showHeroImage && story["hero-image-s3-key"] ? (
          <div styleName={`hero-image-wrapper ${story?.subheadline !== null ? "" : "no-subHeadline-wrapper"}`}>
            {/* {!isAdfreePage && isMagazineStoryWrapper && (
              <DfpComponent
                adStyleName="ad-slot-size-300x250"
                id="magazine-story-page-right-mrec"
                path={`${networkId}/RPSG_Opn_Mob/RPSG_Opn_AS_mREC`}
                adSlotConfigName="mRec_ad"
                showText={true}
              />
            )} */}
            <HeroImage
              story={story}
              aspectRatio={[[16, 9]]}
              isStoryPageImage={true}
              showIcon={false}
              isStoryHeader={true}
            />
          </div>
        ) : null}
        {showHeroImage ? <CaptionAttribution story={story} /> : null}
        {isMobile && isMagazineStoryWrapper && (
          <>
            <br />
            <MagazineStoryHeaderCard story={story} magazineDetails={magazineDetails} isMagazineRemove />
          </>
        )}
      </div>
      {shouldShowSummary && showSummary && (
        <SummaryCard
          setShowSummary={setShowSummary}
          story={story}
          summaryIndex={summaryIndex}
          summaryHtml={shouldUseApiSummary ? summary : ""}
          summaryTitle={shouldUseApiSummary ? apiHeadline : ""}
          summaryImage={shouldUseApiSummary ? apiHeroImageS3Key : ""}
        />
      )}
      {showSummary && <div styleName="blur-bg" />}
    </React.Fragment>
  );
};

StoryHeader.defaultProps = {
  showAd: true,
  headline: "",
  storyAuthorData: [],
  publishTime: 0,
  readTime: 0,
  showHeroImage: true,
  showReview: false,
  showBookReview: false,
  isValidBookReview: false,
};

StoryHeader.propTypes = {
  showAd: bool,
  showHeroImage: bool,
  headline: string.isRequired,
  storyAuthorData: array.isRequired,
  publishTime: number,
  readTime: number,
  story: object,
  magazineDetails: object,
  setShowHighlights: func,
  showReview: bool,
  showBookReview: bool,
  isValidBookReview: bool,
};

export default StoryHeader;
